import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {FeatureService, FeatureTypeService, NotificationsService, ProjectService} from '@catcore/services';
import {GenericLinkedResource} from '@catcore/types/base';
import {GenericResourceService} from '@catfeatures/services/generic-resource.service';
import {ParameterListComponent} from '@catmodels/parameters/components/parameter-list/parameter-list.component';
import {ModifyGgwDeviceParamComponent} from '@catmodules/models/ggw-device-params/components/modify-ggw-device-param/modify-ggw-device-param.component';
import {GgwDeviceParam} from '@catmodules/models/ggw-device-params/types';
import {PanelLinkedListComponent} from '@catui/components/panel-linked-list/panel-linked-list.component';
import {LinkItem, StyleType} from '@catui/types';
import {ConfirmationService} from 'primeng/primeng';
import {Subscription} from 'rxjs/Subscription';
import {isNullOrUndefined} from 'util';
import {extractDataFromModel, genericResourceToLinkItem} from '../../../../core/utils';
import {ModalService} from '../../../../widgets/slide-in-modal/services/modal.service';

@Component({
  selector: 'cat-ggw-device-param-list',
  templateUrl: './ggw-device-param-list.component.html',
  styleUrls: ['./ggw-device-param-list.component.scss'],
})
export class GgwDeviceParamListComponent extends ParameterListComponent implements OnInit, OnDestroy {

  private parameterOptionsSubscription;

  @Output() afterUpdate = new EventEmitter<GgwDeviceParam>();

  @Input() featureLevel = false;
  @Input() feature = null;
  @Input() project = null;

  private projectSubscription: Subscription;
  private featureSubscription: Subscription;

  displayCreateFromXmlDialog = false;

  ggwParameterOptions: any = null;

  private updatingParamOptions = false;

  constructor(protected modalService: ModalService,
              protected genericService: GenericResourceService,
              private featureService: FeatureTypeService,
              protected confirmationService: ConfirmationService,
              protected notificationService: NotificationsService,
              private projectService: ProjectService) {
    super(modalService, genericService, confirmationService, notificationService);
  }

  ngOnInit() {
    if (this.featureLevel) {
      this.featureSubscription = this.featureService
        .getCurrent()
        .subscribe((feature) => {
          this.feature = feature;
          this.updateLinkedParameterTypeOptions();
        });
    } else {
      this.projectSubscription = this.projectService
        .getCurrentProject()
        .subscribe((project) => {
          this.project = project;
          this.updateLinkedParameterTypeOptions();
        });
    }
  }

  ngOnDestroy() {
    if (this.featureSubscription) {
      this.featureSubscription.unsubscribe();
    }
    if (this.projectSubscription) {
      this.projectSubscription.unsubscribe();
    }
    if (this.parameterOptionsSubscription) {
      this.parameterOptionsSubscription.unsubscribe();
    }
  }

  /**
   * Updates the options for the given parameter type. If the parameter type is not provided or set to null,
   * the options are initialized so that the allowed parameter types are available.
   *
   * If the options for the given parameter type have already been loaded, they are not loaded again.
   *
   * The structure of the field ggwParameterOptions after the options have been loaded is the following:
   *
   * ggwParameterOptions = {
   *   type_options: [
   *     {value: 'ggw_impl', label: 'GGW Impl'},
   *     {value: '...', label: '...'},
   *     ...
   *   ],
   *   some_not_yet_loaded_param_type: {},
   *   some_loaded_param_type: {
   *     'all': [
   *       {value: '...', label: '...', 'attribute_name': '...},
   *       ...
   *     ],
   *     'some_category': [
   *       {value: '...', label: '...', 'attribute_name': '...},
   *       ...
   *     ],
   *     'some_other_category': [
   *       {value: '...', label: '...', 'attribute_name': '...},
   *       ...
   *     ]
   *   },
   *   other_param_type: {
   *     'all': [
   *       ...
   *     ],
   *     ...
   *   }
   * }
   *
   * @param parameterType
   */
  updateLinkedParameterTypeOptions(parameterType = null) {

    if (this.updatingParamOptions) {
      // Already updating
      return;
    }

    if (this.ggwParameterOptions !== null && !this.ggwParameterOptions.hasOwnProperty(parameterType)) {
      // Parameter is not a linked parameter
      return;
    }

    if (this.ggwParameterOptions !== null
      && (parameterType === null || this.ggwParameterOptions[parameterType].hasOwnProperty('all'))) {
      // Already loaded
      return;
    }

    this.updatingParamOptions = true;

    if (this.parameterOptionsSubscription) {
      this.parameterOptionsSubscription.unsubscribe();
    }
    if (this.featureLevel) {
      if (this.feature && !isNullOrUndefined(this.feature.id)) {
        this.parameterOptionsSubscription = this.featureService
          .getGgwParameterOptions(this.feature.id, parameterType)
          .subscribe((optionsByType) => {
            this.updateLinkedParameterOptions(parameterType, optionsByType);
            this.updatingParamOptions = false;
          });
      } else {
        this.updatingParamOptions = false;
      }

    } else {
      if (this.project && !isNullOrUndefined(this.project.id)) {
        this.parameterOptionsSubscription = this.projectService
          .getGgwParameterOptions(this.project, parameterType)
          .subscribe((optionsByType) => {
            this.updateLinkedParameterOptions(parameterType, optionsByType);
            this.updatingParamOptions = false;
          });
      } else {
        this.updatingParamOptions = false;
      }
    }
  }

  protected createLinkedParameterList(resourceParams: GenericLinkedResource[]) {
    return genericResourceToLinkItem(resourceParams, (value: GenericLinkedResource) => (router: Router) => this.openModifyGgwDeviceParamDialog(value));
  }

  createParam() {
    const compRef = this.modalService.open(ModifyGgwDeviceParamComponent);
    compRef.componentInstance.enableDelete = false;
    compRef.componentInstance.featureLevel = this.featureLevel;
    compRef.componentInstance.feature = this.feature;
    compRef.componentInstance.ggwParameterOptions = this.ggwParameterOptions;
    compRef.componentInstance.ggwDeviceUri = this._resource._links.self.href;
    compRef.componentInstance.updateOptionsForParamType.subscribe((parameterType) => this.updateLinkedParameterTypeOptions(parameterType));
    compRef.componentInstance.afterUpdate.subscribe((updatedNetworkIf) => this.afterUpdate.emit(updatedNetworkIf));
    return compRef;
  }

  createParamsFromXML() {
    this.displayCreateFromXmlDialog = true;
  }

  private updateLinkedParameterOptions(parameterType, optionsByType) {
    if (!this.ggwParameterOptions) {
      this.ggwParameterOptions = optionsByType;
      optionsByType['type_options'].forEach((option) => {
        this.ggwParameterOptions[option.value] = {};
      });
    }
    if (parameterType && optionsByType[parameterType]) {
      this.ggwParameterOptions[parameterType] = optionsByType[parameterType];
    }
  }

  private openModifyGgwDeviceParamDialog(resource: GenericLinkedResource) {
    this.openDialog(resource, (res) => this.ggwDeviceParamModalFactory(res));
  }

  private ggwDeviceParamModalFactory(parameter) {
    console.warn(parameter);
    if (this.ggwParameterOptions.hasOwnProperty(parameter.parameter_type)) {
      this.updateLinkedParameterTypeOptions(parameter.parameter_type);
    }
    const compRef = this.modalService.open(ModifyGgwDeviceParamComponent);
    compRef.componentInstance.parameter = parameter;
    compRef.componentInstance.enableDelete = true;
    compRef.componentInstance.featureLevel = this.featureLevel;
    compRef.componentInstance.feature = this.feature;
    compRef.componentInstance.ggwParameterOptions = this.ggwParameterOptions;
    compRef.componentInstance.updateOptionsForParamType.subscribe((parameterType) => this.updateLinkedParameterTypeOptions(parameterType));
    compRef.componentInstance.afterUpdate.subscribe((updatedNetworkIf) => this.afterUpdate.emit(updatedNetworkIf));
    compRef.componentInstance.afterDelete.subscribe(() => this.afterDelete.emit());
    return compRef;
  }

}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_master@2/app/Resources/Public/src/app/modules/models/ggw-device-params/components/ggw-device-param-list/ggw-device-param-list.component.ts